import { getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { useGetConversionInfluenceInsightsMutation } from 'app/apis/queryApi';
import { getDateRangeAsObjects } from 'features/date-range-selection/dateRangeSlice';
import { selectSiteId } from 'features/topbar/selectedSiteSlice';
import { useDispatch, useSelector } from 'react-redux';
import SegmentControls from 'features/segment-controls/segment-controls/segment-controls';
import { RootState } from 'app/store';
import {
  selectCanRunQuery,
  selectIsDirty,
  selectSegmentFilters,
  selectSegmentsOrdered
} from 'features/segment-controls/segment-controls.slice';
import {
  InsightQueryRequestDto,
  InsightsMetricRowsResult
} from 'features/insights/types/insights.types';
import { createInsightsReportRequest } from 'features/segment-controls/factories/query-segments-request-factory';
import { InsightTypes } from 'features/insights/constants/insight-types';
import QuerySegmentsButton from 'features/segment-controls/query-segments/query-segments-button';
import ResetSegmentsButton from 'features/segment-controls/reset-segments/reset-segments-button';
import Tile from 'features/tiles/tile';
import { QueryStatus } from '@reduxjs/toolkit/query';
import { useMemo } from 'react';
import { resetInsights } from 'features/insights/insights.middleware';
import { DrillthroughTable } from 'common/components/table/drillthrough-table/drillthrough-table';
import { DrillthroughTableInjectableRow } from 'common/components/table/drillthrough-table/drillthrough-table.types';
import { createDrillthroughInsightsTableInjectableRow } from 'features/advanced-reports/table/factories/advanced-report-drillthrough-table-injectable-row-factory';
import { selectOpenRowIds } from 'features/insights/conversion-influence/conversion-influence-table.slice';
import { getConversionInfluenceInsightColumnDefs } from 'features/insights/conversion-influence/conversion-influence-column-defs';
import { createInsightRows } from 'features/insights/insights-table-row-factory';

const ConversionInfluenceInsightPage = () => {
  const componentId = 'ConversionInfluence';
  const dispatch = useDispatch();
  const dates = useSelector(getDateRangeAsObjects);
  const siteId = useSelector(selectSiteId);
  const maxInsightSegments = 2;
  const canRunQuery = useSelector((state: RootState) =>
    selectCanRunQuery(state, componentId)
  );
  const openRowIds = useSelector(selectOpenRowIds);
  const isDirty = useSelector((state: RootState) =>
    selectIsDirty(state, componentId)
  );

  const activeSegments = useSelector((state: RootState) =>
    selectSegmentsOrdered(state, componentId)
  );

  const activeSegmentFilters = useSelector((state: RootState) =>
    selectSegmentFilters(state, componentId)
  );

  const createRequest = (): InsightQueryRequestDto => {
    return createInsightsReportRequest(
      siteId,
      InsightTypes.CONVERSION_INFLUENCE,
      activeSegments,
      activeSegmentFilters ?? [],
      dates
    );
  };

  const [getConversionInfluenceInsightData, conversionInfluenceInsightResult] =
    useGetConversionInfluenceInsightsMutation();

  const columnDefs = getConversionInfluenceInsightColumnDefs(
    conversionInfluenceInsightResult.data,
    conversionInfluenceInsightResult.isLoading
  );

  const runGetConversionInfluenceInsightMutation = () => {
    void getConversionInfluenceInsightData(createRequest());
  };

  const isButtonEnabled =
    canRunQuery && !conversionInfluenceInsightResult.isLoading;

  const isGettingQuery =
    conversionInfluenceInsightResult.status === QueryStatus.pending;

  const tableData: InsightsMetricRowsResult = useMemo(() => {
    const data: InsightsMetricRowsResult = {
      insights: conversionInfluenceInsightResult.data ?? []
    };

    return data;
  }, [conversionInfluenceInsightResult]);

  const metricRows = useMemo(
    () =>
      createInsightRows(tableData).sort(
        (a, b) => b.occurrences.primaryValue - a.occurrences.primaryValue
      ),
    [tableData]
  );

  const rootRows = useMemo(
    () => metricRows.filter((row) => row.parentId === ''),
    [metricRows]
  );

  const drillthroughRows = useMemo(() => {
    const rows = metricRows
      .filter((row) => openRowIds.includes(row.parentId))
      .map<DrillthroughTableInjectableRow>((row) =>
        createDrillthroughInsightsTableInjectableRow(
          row,
          columnDefs.map((column) => column.id!)
        )
      );
    return rows;
  }, [metricRows, openRowIds, columnDefs]);

  const table = useReactTable({
    data: rootRows,
    columns: columnDefs,
    getCoreRowModel: getCoreRowModel()
  });

  const onConfirmResetSegments = () => {
    conversionInfluenceInsightResult.reset();
    dispatch(resetInsights());
  };

  const hasQueryRun =
    conversionInfluenceInsightResult.status !== QueryStatus.uninitialized;

  return (
    <>
      <Tile className="grow mb-3">
        <SegmentControls componentId={componentId} limit={maxInsightSegments} />
        <div className="mt-2 flex items-center">
          <QuerySegmentsButton
            enabled={isButtonEnabled}
            isLoading={isGettingQuery}
            onClick={runGetConversionInfluenceInsightMutation}
          />
          <div className="ml-2">
            <ResetSegmentsButton
              componentId={componentId}
              onConfirmModal={onConfirmResetSegments}
            />
          </div>
        </div>
      </Tile>

      <Tile>
        {isDirty && hasQueryRun ? (
          <div className="flex items-center mb-2.5 border border-warning-600 bg-warning-100 px-2 py-1">
            <i className="ra-icons-generic-warning-2 text-xl text-warning-600 flex"></i>
            <div className="ml-1.5 flex flex-col">
              <p className="text-xs">
                These table results no longer reflect the segment controls.
                Re-run the query to see the results.
              </p>
            </div>
          </div>
        ) : (
          <></>
        )}
        <div className={`${isDirty && hasQueryRun ? 'opacity-60' : ''}`}>
          <DrillthroughTable
            key={`conversion-influence-table`}
            componentId={'conversion-influence-table'}
            heightClass="max-h-2/3-screen"
            table={table}
            injectableRows={drillthroughRows}
          />
        </div>
      </Tile>
    </>
  );
};

export default ConversionInfluenceInsightPage;
